import React from 'react';
import {
  DashboardOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  AccountBookOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  HistoryOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { NavItemConfig } from '@/lib/navigation';
import { RoleName } from '@/lib/auth';
import { ModuleManifest, ModuleTier, PatchHistoryEntry } from './types';
import { ACTIVE_ADDONS } from './activeAddons';

/**
 * Baseline Core Manifest for Paket 1
 */
export const CORE_MODULE_MANIFEST: ModuleManifest = {
  id: 'procurement',
  name: 'Paket 1: Procurement & Accounts Payable',
  tier: 1,
  version: '1.0.0',
  description:
    'Siklus Pengadaan Lengkap: PR Line-Item Approval, PO Konsolidasi, Goods Receipt Gudang (Zero Over-delivery), FIFO Cost Layers, dan Finance AP 3-Way Matching dengan Payment Voucher Multi-PO.',
  dependencies: [],
  isCore: true,
  routes: [
    '/',
    '/purchase-requests',
    '/purchase-orders',
    '/finance',
    '/inventory',
    '/reports',
    '/master',
    '/admin',
  ],
  navigation: [
    {
      key: 'dashboard',
      label: 'Dashboard',
      path: '/',
      icon: React.createElement(DashboardOutlined),
    },
    {
      key: 'purchase-requests',
      label: 'Pengajuan Pembelian',
      path: '/purchase-requests',
      icon: React.createElement(FileTextOutlined),
      allowedRoles: ['ADMIN', 'OWNER', 'PURCHASER'],
    },
    {
      key: 'purchase-orders',
      label: 'Pesanan Pembelian',
      path: '/purchase-orders',
      icon: React.createElement(ShoppingOutlined),
      allowedRoles: ['ADMIN', 'OWNER', 'PURCHASER', 'WAREHOUSE'],
    },
    {
      key: 'finance',
      label: 'Tagihan & Pembayaran',
      path: '/finance',
      icon: React.createElement(AccountBookOutlined),
      allowedRoles: ['ADMIN', 'OWNER', 'FINANCE_ADMIN', 'FINANCE_MANAGER'],
    },
    {
      key: 'inventory',
      label: 'Gudang & Inventori',
      path: '/inventory',
      icon: React.createElement(DatabaseOutlined),
      allowedRoles: ['ADMIN', 'OWNER', 'PURCHASER', 'WAREHOUSE'],
      children: [
        {
          key: 'inventory-stock',
          label: 'Stok Barang',
          path: '/inventory',
          icon: React.createElement(DatabaseOutlined),
          allowedRoles: ['ADMIN', 'OWNER', 'PURCHASER', 'WAREHOUSE'],
        },
        {
          key: 'inventory-transactions',
          label: 'Mutasi Stok',
          path: '/inventory/transactions',
          icon: React.createElement(HistoryOutlined),
          allowedRoles: ['ADMIN', 'OWNER', 'PURCHASER', 'WAREHOUSE'],
        },
        {
          key: 'inventory-catalog',
          label: 'Katalog Bahan Baku',
          path: '/inventory/catalog-summary',
          icon: React.createElement(AppstoreOutlined),
          allowedRoles: ['ADMIN', 'OWNER', 'PURCHASER', 'WAREHOUSE'],
        },
      ],
    },
    {
      key: 'reports',
      label: 'Laporan & Analitik',
      path: '/reports',
      icon: React.createElement(BarChartOutlined),
      allowedRoles: ['ADMIN', 'OWNER', 'PURCHASER', 'WAREHOUSE'],
    },
    {
      key: 'master-data',
      label: 'Data Master',
      path: '/master',
      icon: React.createElement(AppstoreOutlined),
      allowedRoles: ['ADMIN', 'PURCHASER', 'WAREHOUSE'],
      children: [
        {
          key: 'master-items',
          label: 'Bahan & Material',
          path: '/master/items',
          allowedRoles: ['ADMIN', 'PURCHASER', 'WAREHOUSE'],
        },
        {
          key: 'master-suppliers',
          label: 'Daftar Supplier',
          path: '/master/suppliers',
          allowedRoles: ['ADMIN', 'PURCHASER'],
        },
        {
          key: 'master-uom',
          label: 'Satuan Ukuran',
          path: '/master/uom',
          allowedRoles: ['ADMIN', 'PURCHASER', 'WAREHOUSE'],
        },
        {
          key: 'master-warehouses',
          label: 'Daftar Gudang',
          path: '/master/warehouses',
          icon: React.createElement(HomeOutlined),
          allowedRoles: ['ADMIN', 'WAREHOUSE'],
        },
      ],
    },
    {
      key: 'admin',
      label: 'Pengaturan Sistem',
      path: '/admin',
      icon: React.createElement(SettingOutlined),
      allowedRoles: ['ADMIN'],
      children: [
        {
          key: 'admin-modules',
          label: 'Lisensi & Paket',
          path: '/admin/modules',
          allowedRoles: ['ADMIN'],
        },
        {
          key: 'admin-users',
          label: 'Pengguna & Akses',
          path: '/admin/users',
          allowedRoles: ['ADMIN'],
        },
        {
          key: 'admin-audit',
          label: 'Log Aktivitas',
          path: '/admin/audit-logs',
          icon: React.createElement(SafetyCertificateOutlined),
          allowedRoles: ['ADMIN'],
        },
      ],
    },
  ],
};

/**
 * Registry of dynamic addon modules.
 * In a patched build, installed modules are registered here.
 */
let installedAddonModules: ModuleManifest[] = [];

export function registerAddonModule(module: ModuleManifest) {
  const existingIdx = installedAddonModules.findIndex((m) => m.id === module.id);
  if (existingIdx >= 0) {
    installedAddonModules[existingIdx] = module;
  } else {
    installedAddonModules.push(module);
  }
}

export function unregisterAddonModule(moduleId: string) {
  installedAddonModules = installedAddonModules.filter((m) => m.id !== moduleId);
}

/**
 * Returns all installed and active modules
 */
export function getInstalledModules(): ModuleManifest[] {
  return [CORE_MODULE_MANIFEST, ...ACTIVE_ADDONS, ...installedAddonModules].sort((a, b) => a.tier - b.tier);
}

/**
 * Calculates current active Tier
 */
export function getActiveTier(): ModuleTier {
  const all = getInstalledModules();
  return Math.max(...all.map((m) => m.tier)) as ModuleTier;
}

/**
 * Consolidates navigation items across all active modules
 */
export function getConsolidatedNavigation(): NavItemConfig[] {
  const modules = getInstalledModules();
  const baseNav = [...CORE_MODULE_MANIFEST.navigation];

  // Inject addon navigations before Reports or Admin
  for (const mod of modules) {
    if (mod.isCore || !mod.navigation) continue;

    // Find insertion index before 'reports' or 'master-data'
    const insertIdx = baseNav.findIndex((item) => item.key === 'reports');
    if (insertIdx >= 0) {
      baseNav.splice(insertIdx, 0, ...mod.navigation);
    } else {
      baseNav.push(...mod.navigation);
    }
  }

  return baseNav;
}
